import { useState } from "react";
import { toast } from "sonner";
import { researchCategoryClasses } from "#shared/researchCategoryColors.ts";
import { Button } from "@web/components/ui/button.tsx";
import { Checkbox } from "@web/components/ui/checkbox.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import {
  useCreateResearchCategory,
  useResearchCategories,
  useUpdateResearchCategory,
} from "@web/features/ideas/ideasApi.ts";
import { cn } from "@web/lib/utils.ts";

/**
 * Per-user research angles — drives {{CATEGORIES}} in clipboard prompts.
 * Deactivate hides from future prompts; rename rewrites card tags.
 */
export function ResearchCategoriesPanel() {
  const cats = useResearchCategories();
  const updateCat = useUpdateResearchCategory();
  const createCat = useCreateResearchCategory();

  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [questionDraft, setQuestionDraft] = useState("");
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState("");
  const [newName, setNewName] = useState("");
  const [newQuestion, setNewQuestion] = useState("");

  return (
    <section className="rounded-lg border border-line bg-panel px-4 py-4">
      <h2 className="text-sm font-semibold">Research categories</h2>
      <p className="mt-1 text-sm text-muted">
        Active angles are injected into Research / Continue prompts. Deactivate to hide from
        future copies — existing Compose cards keep their tags. Rename updates those tags.
        Discovered (from AI ingest) start inactive.
      </p>

      {cats.isLoading && (
        <div className="mt-4 space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {cats.isSuccess && (
        <ul className="mt-4 divide-y divide-line">
          {cats.data.map((cat) => {
            const colors = researchCategoryClasses(cat.name);
            const editingQ = editingQuestionId === cat.id;
            const editingN = editingNameId === cat.id;
            return (
              <li key={cat.id} className="flex items-start gap-3 py-3">
                <Checkbox
                  checked={cat.active}
                  disabled={updateCat.isPending}
                  onCheckedChange={(v) =>
                    updateCat.mutate({ id: cat.id, active: v === true })
                  }
                  aria-label={`Active: ${cat.name}`}
                  className="mt-1"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {editingN ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          value={nameDraft}
                          onChange={(e) => setNameDraft(e.target.value)}
                          className="h-8 w-40 rounded-md border border-line bg-panel-elevated px-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                          aria-label="Category name"
                        />
                        <Button
                          type="button"
                          size="sm"
                          disabled={!nameDraft.trim() || updateCat.isPending}
                          onClick={() =>
                            updateCat.mutate(
                              { id: cat.id, name: nameDraft.trim() },
                              {
                                onSuccess: () => {
                                  setEditingNameId(null);
                                  toast.success("Category renamed (cards updated)");
                                },
                              },
                            )
                          }
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingNameId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={cn(
                          "rounded-sm border px-1.5 py-0.5 text-xs font-semibold tracking-wider uppercase",
                          colors.text,
                          colors.bg,
                          colors.border,
                          "hover:opacity-90",
                        )}
                        title="Rename category"
                        onClick={() => {
                          setEditingNameId(cat.id);
                          setNameDraft(cat.name);
                          setEditingQuestionId(null);
                        }}
                      >
                        {cat.name}
                      </button>
                    )}
                    <span
                      className={cn(
                        "text-xs font-semibold tracking-wide uppercase",
                        cat.origin === "discovered" ? "text-research" : "text-faint",
                      )}
                    >
                      {cat.origin === "discovered" ? "new · discovered" : cat.origin}
                      {!cat.active ? " · off" : ""}
                    </span>
                  </div>
                  {editingQ ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={questionDraft}
                        onChange={(e) => setQuestionDraft(e.target.value)}
                        className="min-h-16 w-full resize-y rounded-md border border-line bg-panel-elevated px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={!questionDraft.trim() || updateCat.isPending}
                          onClick={() =>
                            updateCat.mutate(
                              { id: cat.id, question: questionDraft.trim() },
                              {
                                onSuccess: () => {
                                  setEditingQuestionId(null);
                                  toast.success("Question updated");
                                },
                              },
                            )
                          }
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingQuestionId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="mt-1 w-full text-left text-sm text-muted hover:text-text"
                      title="Edit question text"
                      onClick={() => {
                        setEditingQuestionId(cat.id);
                        setQuestionDraft(cat.question);
                        setEditingNameId(null);
                      }}
                    >
                      {cat.question}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-5 rounded-md border border-dashed border-line bg-panel-elevated px-3 py-3">
        <p className="text-xs font-semibold tracking-wider text-faint uppercase">Add category</p>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="name (e.g. lyrics)"
            className="h-8 min-w-0 flex-1 rounded-md border border-line bg-panel px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
          />
          <input
            type="text"
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            placeholder="Question injected into the prompt"
            className="h-8 min-w-0 flex-[2] rounded-md border border-line bg-panel px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
          />
          <Button
            type="button"
            size="sm"
            disabled={!newName.trim() || !newQuestion.trim() || createCat.isPending}
            onClick={() =>
              createCat.mutate(
                {
                  name: newName.trim(),
                  question: newQuestion.trim(),
                  active: true,
                },
                {
                  onSuccess: () => {
                    setNewName("");
                    setNewQuestion("");
                  },
                },
              )
            }
          >
            Add
          </Button>
        </div>
      </div>
    </section>
  );
}
